<script lang="ts">
  import Navbar from "$lib/components/Navbar.svelte";
  import { auth } from "$lib/stores/auth.svelte";
  import { api } from "$lib/api/client";
  import { fmtBRL } from "$lib/utils/format";
  import { navigate } from "$lib/router.svelte";
  import { toasts } from "$lib/stores/toasts.svelte";

  type Recurrence = {
    id: string;
    template: Record<string, any>;
    frequency: string;
    interval: number;
    next_run_at: string;
    end_at: string | null;
    created_at: string;
  };

  let recurrences = $state<Recurrence[]>([]);
  let loading = $state(true);
  let deleting = $state<string | null>(null);

  async function load() {
    loading = true;
    try {
      recurrences = await api<Recurrence[]>("/recurrences");
    } catch {
      recurrences = [];
    } finally {
      loading = false;
    }
  }

  $effect(() => { if (auth.workspaces[0]) load(); });
  $effect(() => {
    function onWsChange() { load(); }
    window.addEventListener("takite:workspace-changed", onWsChange);
    return () => window.removeEventListener("takite:workspace-changed", onWsChange);
  });

  async function deleteRec(id: string) {
    if (!confirm("Excluir esta recorrência? As transações já geradas não serão removidas.")) return;
    deleting = id;
    try {
      await api(`/recurrences/${id}`, { method: "DELETE" });
      recurrences = recurrences.filter(r => r.id !== id);
      toasts.success("Recorrência removida");
    } catch (e: any) {
      toasts.error("Erro", e?.body?.detail || e?.message);
    } finally { deleting = null; }
  }

  // Agrupa por tipo
  type Group = { label: string; emoji: string; items: Recurrence[] };

  function classify(r: Recurrence): string {
    const tpl = r.template || {};
    if (tpl.installment_total) return "parcelas";
    if (tpl.kind === "income") return "receitas";
    return "despesas";
  }

  let groups = $derived((): Group[] => {
    const parcelas = recurrences.filter(r => classify(r) === "parcelas");
    const receitas = recurrences.filter(r => classify(r) === "receitas");
    const despesas = recurrences.filter(r => classify(r) === "despesas");
    return [
      { label: "Despesas Fixas", emoji: "💸", items: despesas },
      { label: "Parcelas", emoji: "📆", items: parcelas },
      { label: "Receitas Fixas", emoji: "💰", items: receitas },
    ].filter(g => g.items.length > 0);
  });

  // Totais mensais
  let totalMensal = $derived(() => {
    return recurrences
      .filter(r => r.frequency === "monthly" && r.template?.kind !== "income")
      .reduce((acc, r) => acc + Number(r.template?.amount || 0), 0);
  });

  let totalReceitas = $derived(() => {
    return recurrences
      .filter(r => r.template?.kind === "income")
      .reduce((acc, r) => acc + Number(r.template?.amount || 0), 0);
  });

  let economiaAnual = $derived(() => totalMensal() * 12);

  function freqLabel(r: Recurrence): string {
    const map: Record<string, string> = {
      daily: "Diário", weekly: "Semanal", monthly: "Mensal", yearly: "Anual",
    };
    const base = map[r.frequency] || r.frequency;
    return r.interval > 1 ? `A cada ${r.interval} ${base.toLowerCase()}s` : base;
  }

  function nextRunLabel(r: Recurrence): string {
    const dt = new Date(r.next_run_at);
    return dt.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
  }

  function installmentProgress(r: Recurrence): { current: number; total: number } | null {
    const tpl = r.template || {};
    if (!tpl.installment_total) return null;
    return { current: tpl.installment_next - 1 || 0, total: tpl.installment_total };
  }
</script>

<Navbar />
<main class="max-w-4xl mx-auto px-4 py-6 space-y-6">
  <div class="flex items-center justify-between">
    <div>
      <h1 class="text-2xl font-bold">🔄 Recorrências</h1>
      <p class="text-muted text-sm">Assinaturas, parcelas e receitas fixas</p>
    </div>
    <button class="btn-primary" onclick={() => navigate("/transactions")}>
      + Nova recorrência
    </button>
  </div>

  <!-- Cards de resumo -->
  {#if !loading && recurrences.length > 0}
    <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <div class="card">
        <div class="text-xs text-muted uppercase tracking-wide mb-1">💸 Saída mensal fixa</div>
        <div class="text-2xl font-bold text-red-500">{fmtBRL(totalMensal())}</div>
        <div class="text-xs text-muted mt-1">≈ {fmtBRL(economiaAnual())} por ano</div>
      </div>
      <div class="card">
        <div class="text-xs text-muted uppercase tracking-wide mb-1">💰 Entrada mensal fixa</div>
        <div class="text-2xl font-bold text-emerald-500">{fmtBRL(totalReceitas())}</div>
      </div>
      <div class="card">
        <div class="text-xs text-muted uppercase tracking-wide mb-1">📋 Total de recorrências</div>
        <div class="text-2xl font-bold">{recurrences.length}</div>
      </div>
    </div>
  {/if}

  {#if loading}
    <div class="space-y-3">
      {#each [1,2,3] as _}
        <div class="skeleton h-16 rounded-xl"></div>
      {/each}
    </div>
  {:else if recurrences.length === 0}
    <div class="card text-center py-12 space-y-3">
      <div class="text-5xl">🔄</div>
      <p class="text-muted text-sm">Nenhuma recorrência cadastrada ainda.</p>
      <p class="text-xs text-muted">
        Ao criar uma transação, marque como recorrente para ela aparecer aqui automaticamente.
      </p>
      <button class="btn-primary mx-auto" onclick={() => navigate("/transactions")}>
        Criar transação recorrente
      </button>
    </div>
  {:else}
    {#each groups() as group}
      <section class="space-y-2">
        <h2 class="font-semibold text-sm text-muted uppercase tracking-wide">
          {group.emoji} {group.label}
        </h2>
        <div class="space-y-2">
          {#each group.items as rec}
            {@const progress = installmentProgress(rec)}
            <div class="card flex items-center justify-between gap-4">
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                  <span class="font-medium truncate">{rec.template?.description || "Sem descrição"}</span>
                  <span class="text-xs px-2 py-0.5 rounded-full" style="background: var(--surface-alt)">
                    {freqLabel(rec)}
                  </span>
                </div>
                <div class="text-xs text-muted mt-0.5 flex items-center gap-3 flex-wrap">
                  <span>Próxima: {nextRunLabel(rec)}</span>
                  {#if rec.end_at}
                    <span>Fim: {new Date(rec.end_at).toLocaleDateString("pt-BR")}</span>
                  {/if}
                  {#if progress}
                    <span class="text-brand font-medium">{progress.current}/{progress.total} parcelas</span>
                  {/if}
                </div>
                {#if progress}
                  <div class="mt-1.5 h-1 rounded-full overflow-hidden w-40" style="background: var(--border)">
                    <div class="h-full bg-brand transition-all"
                         style="width: {(progress.current / progress.total) * 100}%"></div>
                  </div>
                {/if}
              </div>
              <div class="text-right flex-none">
                <div class="font-bold {rec.template?.kind === 'income' ? 'text-emerald-500' : 'text-red-500'}">
                  {fmtBRL(Number(rec.template?.amount || 0))}
                </div>
                <button
                  class="text-xs text-muted hover:text-red-500 transition-colors mt-1"
                  onclick={() => deleteRec(rec.id)}
                  disabled={deleting === rec.id}
                >
                  {deleting === rec.id ? "Removendo…" : "Remover"}
                </button>
              </div>
            </div>
          {/each}
        </div>
      </section>
    {/each}
  {/if}
</main>
